import type { Option } from "@tento-lona/sheets";
import { type Backend, Json, RowId, type RowLocalId, type RowNetworkKey } from "@tento-lona/sheets";
import type { Network } from "../network.ts";
import type { Sheet } from "@tento-lona/sheets/platform";
import { RowWrites, sheetWiring } from "@tento-lona/sheets/internal-sdk";

/**
 * **SDK-internal**: the SDK's side of the client's ONE row-field ledger
 * (`RowWrites`, held by `SheetModelCache.rowWrites`) — what every
 * `SheetRow.Handle` and `SheetRow.Joined.Handle` writes a row's own fields
 * through.
 *
 * - The SDK's display copy of a row: the `SheetRow.raw` of every `SheetRow`
 *   the client holds for its `RowLocalId` (the registry's row, and a
 *   standalone row's — `client.rows.open`, a pending draft — once it wrote,
 *   `hold`). The ledger reads a write's confirmed base from it first (it
 *   holds every field the wire carries) and shows every projection into it,
 *   silently; the ledger announces. A new label re-derives the lookup maps
 *   and formula graph of every open handle holding the row (`Hooks.relabel`),
 *   a new lookup key re-registers the row (`Hooks.lookupKey`).
 * - The one transport of a row write: `Backend.updateRow` of the client's
 *   HTTP rows backend (`transport`), the row addressed by its key WHEN the
 *   body is sent (a promote re-keys it).
 */
export class RowFieldWrites {
  readonly ledger: RowWrites;
  readonly #backend: Backend;
  readonly #registry: (id: RowLocalId) => Option<RowFieldWrites.Copy>;
  readonly #hooks: RowFieldWrites.Hooks;
  /** Standalone rows' copies (not in the registry), per row, weakly held. */
  readonly #held = new Map<RowLocalId, Set<WeakRef<RowFieldWrites.Copy>>>();

  constructor(
    ledger: RowWrites,
    backend: Backend,
    registry: (id: RowLocalId) => Option<RowFieldWrites.Copy>,
    hooks: RowFieldWrites.Hooks,
  ) {
    this.ledger = ledger;
    this.#backend = backend;
    this.#registry = registry;
    this.#hooks = hooks;
    ledger.addDisplay({
      peek: (id) => {
        const row = this.#copies(id)[0];
        return row == null ? null : RowFieldWrites.fieldsOf(row);
      },
      show: (id, delta) => this.#show(id, delta),
    }, "first");
  }

  /** Register `row` as a display copy of its row (a standalone row's). */
  hold(row: RowFieldWrites.Copy): void {
    if (this.#registry(row.id) === row) return;
    let refs = this.#held.get(row.id);
    if (refs == null) {
      refs = new Set();
      this.#held.set(row.id, refs);
    }
    for (const ref of refs) if (ref.deref() === row) return;
    refs.add(new WeakRef(row));
  }

  /** The checked transport: `Backend.updateRow` of the row `rowKey()` names when the body is sent. */
  transport(rowKey: () => RowNetworkKey): RowWrites.Transport {
    return (body) => this.#backend.updateRow(RowId.of(rowKey()), body, null);
  }

  /** Every copy of row `id` the client holds: the registry's, then the standalone ones still alive. */
  #copies(id: RowLocalId): RowFieldWrites.Copy[] {
    const out: RowFieldWrites.Copy[] = [];
    const registered = this.#registry(id);
    if (registered != null) out.push(registered);
    const refs = this.#held.get(id);
    if (refs != null) {
      for (const ref of [...refs]) {
        const row = ref.deref();
        if (row == null) refs.delete(ref);
        else if (!out.includes(row)) out.push(row);
      }
      if (refs.size === 0) this.#held.delete(id);
    }
    return out;
  }

  /** Show `delta` on every copy of row `id`, silently; follow a new label or lookup key. */
  #show(id: RowLocalId, delta: RowWrites.Delta): readonly string[] {
    const copies = this.#copies(id);
    const first = copies[0];
    if (first == null) return [];
    const labelBefore = first.raw.label ?? null;
    const lookupKeyBefore = first.raw.lookupKey ?? null;
    const set = delta.set;
    for (const row of copies) {
      const raw = row.raw;
      if (set.label !== undefined) raw.label = set.label ?? null;
      if (set.icon !== undefined) raw.icon = set.icon ?? null;
      if (set.heightPx !== undefined) raw.heightPx = set.heightPx ?? null;
      if (set.formula !== undefined) raw.formula = set.formula ?? null;
      if (set.timeScale != null) raw.timeScale = set.timeScale;
      if (set.lookupKey !== undefined) raw.lookupKey = set.lookupKey ?? null;
      const attrs = delta.attributes;
      if (attrs != null) {
        if (attrs.after == null) {
          raw.attributes = null;
        } else {
          const bag: { [key: string]: Json } = { ...(Json.object(raw.attributes ?? null) ?? {}) };
          for (const key of attrs.keys) {
            const value = attrs.after[key];
            if (value === undefined) delete bag[key];
            else bag[key] = value;
          }
          raw.attributes = bag;
        }
        row.resetCache();
      }
    }
    if (set.label !== undefined && (set.label ?? null) !== labelBefore) {
      this.#hooks.relabel(id, labelBefore, set.label ?? null);
    }
    if (set.lookupKey !== undefined && (set.lookupKey ?? null) !== lookupKeyBefore) {
      this.#hooks.lookupKey(id, lookupKeyBefore, set.lookupKey ?? null);
    }
    return [];
  }
}

/** Types and helpers that go with `RowFieldWrites`. */
export namespace RowFieldWrites {
  /**
   * A display copy of a row the ledger shows writes into: its id, its wire
   * fields (`raw`, written in place) and the cache it drops when its
   * attribute bag changed. Every `SheetRow` is one.
   */
  export interface Copy {
    readonly id: RowLocalId;
    readonly raw: Network.SheetRowDb;
    resetCache(): void;
  }

  /** What the client does when a row's label or lookup key changed. */
  export interface Hooks {
    /** Every open handle holding row `id` re-derives its lookup maps and formula graph. */
    relabel(id: RowLocalId, from: Option<string>, to: Option<string>): void;
    /** Row `id` answers to lookup key `to` (the key `from` released). */
    lookupKey(id: RowLocalId, from: Option<string>, to: Option<string>): void;
  }

  /** Hooks that follow nothing (a tree no client holds). */
  export const NO_HOOKS: Hooks = Object.freeze({
    relabel: () => {},
    lookupKey: () => {},
  });

  /** `row`'s own fields as its `raw` holds them. */
  export function fieldsOf(row: Copy): RowWrites.Fields {
    return fieldsOfWire(row.raw);
  }

  /** A wire row's own fields (`raw`: a row as the server answered it, or a `SheetRow`'s raw). */
  export function fieldsOfWire(raw: Network.SheetRowDb): RowWrites.Fields {
    return {
      label: raw.label ?? null,
      icon: raw.icon ?? null,
      heightPx: raw.heightPx ?? null,
      formula: raw.formula ?? null,
      timeScale: raw.timeScale,
      attributes: Json.object(raw.attributes ?? null),
      lookupKey: raw.lookupKey ?? null,
    };
  }

  /**
   * The row writes of a joined tree no client holds (`SheetRowTree` built
   * without `rowWrites`): the tree's rows-side sheet's own ledger, sent
   * through that sheet's backend, over no client registry. The tree builds
   * one and every row of it writes through it.
   */
  export function ofSheet(sheet: Sheet): RowFieldWrites {
    return new RowFieldWrites(sheetWiring(sheet).rowWrites, sheet.backend, () => null, NO_HOOKS);
  }
}
